import { GovernanceColors } from '@/config/governanceColors';
import { useSecurityStatusData } from '@/hooks/useCommandCenter';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Cpu, Activity, Layers } from 'lucide-react';

export function SecurityView() {
  const { data, loading } = useSecurityStatusData();

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading Security data...</div>;
  }

  const health = data?.health;
  const quantum = data?.quantum;
  const governance = data?.governance;

  const apiStatus = health?.status ?? 'unknown';
  const uptime = health?.uptime ? `${Math.floor(health.uptime / 3600)}h ${Math.floor((health.uptime % 3600) / 60)}m` : '-';
  const memoryMB = health?.memoryUsage?.heapUsed ? Math.round(health.memoryUsage.heapUsed / 1024 / 1024) : '-';
  const version = health?.version ?? '-';

  const quantumBackends = Array.isArray(quantum?.backends)
    ? quantum.backends.length
    : (quantum?.activeBackends ?? 0);
  const quantumStatus = quantum?.status ?? 'unknown';

  const anomalyStatus = governance?.defensePipeline?.anomalyDetector?.active === true ? 'active' : (governance?.defensePipeline?.anomalyDetector?.active === false ? 'inactive' : 'unknown');
  const anomaliesPerHour = governance?.defensePipeline?.anomalyDetector?.anomaliesInLastHour ?? 0;
  const pendingProposals = governance?.defensePipeline?.recursiveLearning?.pendingProposals ?? 0;
  const monitorAlerts = governance?.defensePipeline?.agentMonitor?.totalAlerts ?? governance?.defensePipeline?.agentMonitor?.recentAlerts?.length ?? 0;
  const haltActive = governance?.fleetHalt?.active ?? false;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Security Status</h2>

      {/* Badges */}
      <div className="flex gap-3 flex-wrap">
        <Badge variant="outline" style={apiStatus === 'ok' || apiStatus === 'healthy' ? { color: GovernanceColors.healthy, borderColor: GovernanceColors.healthy } : { color: GovernanceColors.warning, borderColor: GovernanceColors.warning }}>
          Governance {apiStatus === 'ok' || apiStatus === 'healthy' ? 'Operational' : apiStatus}
        </Badge>
        <Badge variant="outline" style={quantumStatus === 'operational' || quantumStatus === 'active' ? { color: GovernanceColors.streaming, borderColor: GovernanceColors.streaming } : { color: '#A8A29E', borderColor: '#4A443D' }}>
          Quantum {quantumStatus}
        </Badge>
        <Badge variant="outline" style={{ color: GovernanceColors.primaryLight, borderColor: GovernanceColors.primaryLight }}>
          97 Policy Rules
        </Badge>
        <Badge variant="outline" className="text-[#A8A29E] border-[#4A443D]">
          9,701 Tests / 345 Suites
        </Badge>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4" style={{ color: GovernanceColors.healthy }} />
              <span className="text-sm font-medium">System Health</span>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-[#A8A29E]">API: <span style={{ color: apiStatus === 'ok' || apiStatus === 'healthy' ? GovernanceColors.healthy : GovernanceColors.warning }}>{apiStatus}</span></p>
              <p className="text-xs text-[#A8A29E]">Uptime: <span className="text-[#F5F0E8]">{uptime}</span></p>
              <p className="text-xs text-[#A8A29E]">Memory: <span className="text-[#F5F0E8]">{memoryMB} MB</span></p>
              <p className="text-xs text-[#A8A29E]">Version: <span className="text-[#F5F0E8]">{version}</span></p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Cpu className="w-4 h-4" style={{ color: GovernanceColors.streaming }} />
              <span className="text-sm font-medium">Quantum Bridge</span>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-[#A8A29E]">Status: <span style={quantumStatus === 'operational' ? { color: GovernanceColors.healthy } : { color: '#A8A29E' }}>{quantumStatus}</span></p>
              <p className="text-xs text-[#A8A29E]">Backends: <span className="text-[#F5F0E8]">{quantumBackends}</span></p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4" style={{ color: GovernanceColors.primary }} />
              <span className="text-sm font-medium">Defense Pipeline</span>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-[#A8A29E]">Anomaly Detector: <span style={anomalyStatus === 'active' ? { color: GovernanceColors.healthy } : { color: '#A8A29E' }}>{anomalyStatus}</span></p>
              <p className="text-xs text-[#A8A29E]">Anomalies/hr: <span className="text-[#F5F0E8]">{anomaliesPerHour}</span></p>
              <p className="text-xs text-[#A8A29E]">Pending Proposals: <span className="text-[#F5F0E8]">{pendingProposals}</span></p>
              <p className="text-xs text-[#A8A29E]">Monitor Alerts: <span style={monitorAlerts > 0 ? { color: GovernanceColors.warning } : { color: '#F5F0E8' }}>{monitorAlerts}</span></p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4" style={{ color: GovernanceColors.primaryLight }} />
              <span className="text-sm font-medium">Policy Coverage</span>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-[#A8A29E]">Policy Rules: <span className="text-[#F5F0E8]">97</span></p>
              <p className="text-xs text-[#A8A29E]">Test Suites: <span className="text-[#F5F0E8]">179</span></p>
              <p className="text-xs text-[#A8A29E]">Total Tests: <span className="text-[#F5F0E8]">9,701</span></p>
              <p className="text-xs text-[#A8A29E]">Fleet Halt: <span style={{ color: haltActive ? GovernanceColors.critical : GovernanceColors.healthy }}>{haltActive ? 'ACTIVE' : 'Inactive'}</span></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
